import React, { useState } from 'react';
import { X, Plus, Trash2, HelpCircle, Loader2 } from 'lucide-react';

interface AdditionalFee {
  id: string;
  name: string;
  amount: number;
}

interface PaymentPlanFormData {
  name: string;
  type: 'single' | 'bundle' | 'subscription' | 'yearly' | 'term';
  price: number;
  currency: string;
  interval: 'one-time' | 'monthly' | 'yearly';
  additionalFees: AdditionalFee[];
  freeTrial: boolean;
  trialDays: number;
  active: boolean;
  autoCancellation: string;
}

interface PaymentPlanModalProps {
  plan?: any;
  onClose: () => void;
  onSave: (data: Partial<PaymentPlanFormData>) => void;
  saving?: boolean;
}

const planTypes = [
  { value: 'single', label: 'Single Course' },
  { value: 'bundle', label: 'Bundle' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'yearly', label: 'Whole Year' },
  { value: 'term', label: 'Term' },
];

const paymentIntervals = [
  { value: 'one-time', label: 'One-time' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const trialPeriods = [
  { value: 0, label: 'No trial' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
];

const autoCancellationOptions = [
  { value: 'none', label: 'Never' },
  { value: '1-month', label: 'After 1 month non-payment' },
  { value: '2-months', label: 'After 2 months non-payment' },
  { value: '3-months', label: 'After 3 months non-payment' },
];

export function PaymentPlanModal({ plan, onClose, onSave, saving = false }: PaymentPlanModalProps) {
  const [formData, setFormData] = useState<PaymentPlanFormData>({
    name: plan?.name || '',
    type: plan?.type || 'single',
    price: plan?.price || 0,
    currency: plan?.currency || '$',
    interval: plan?.interval || 'one-time',
    additionalFees: plan?.additionalFees?.map((f: any, idx: number) => ({ ...f, id: idx.toString() })) || [],
    freeTrial: plan?.freeTrial || false,
    trialDays: plan?.trialDays || 0,
    active: plan?.active ?? true,
    autoCancellation: plan?.autoCancellation || 'none',
  });

  const isEditMode = !!plan;

  const handleAddFee = () => {
    setFormData(prev => ({
      ...prev,
      additionalFees: [...prev.additionalFees, { id: Date.now().toString(), name: '', amount: 0 }],
    }));
  };

  const handleRemoveFee = (feeId: string) => {
    setFormData(prev => ({
      ...prev,
      additionalFees: prev.additionalFees.filter(f => f.id !== feeId),
    }));
  };

  const handleFeeChange = (feeId: string, field: 'name' | 'amount', value: string | number) => {
    setFormData(prev => ({
      ...prev,
      additionalFees: prev.additionalFees.map(f =>
        f.id === feeId ? { ...f, [field]: value } : f
      ),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      additionalFees: formData.additionalFees.map(({ id, ...rest }) => rest) as any,
    });
  };

  const totalPrice = formData.price + formData.additionalFees.reduce((sum, f) => sum + (f.amount || 0), 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 bg-black/50">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditMode ? 'Edit Payment Plan' : 'Create New Payment Plan'}
              </h2>
              {isEditMode && (
                <p className="text-sm text-gray-500">{plan.name}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto">
            <div className="space-y-5">
              {/* General Info Section */}
              <div className="pb-4 border-b border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-4">General Info</h3>

                {/* Plan Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Annual Mathematics Package"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                {/* Plan Type */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {planTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="pb-4 border-b border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-4">Pricing</h3>

                {/* Plan Price */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Payment Interval */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Interval
                  </label>
                  <select
                    value={formData.interval}
                    onChange={(e) => setFormData(prev => ({ ...prev, interval: e.target.value as any }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {paymentIntervals.map(interval => (
                      <option key={interval.value} value={interval.value}>{interval.label}</option>
                    ))}
                  </select>
                </div>

                {/* Additional Fees */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Additional Fees
                    </label>
                    <button
                      type="button"
                      onClick={handleAddFee}
                      className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add More
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.additionalFees.map((fee) => (
                      <div key={fee.id} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={fee.name}
                          onChange={(e) => handleFeeChange(fee.id, 'name', e.target.value)}
                          placeholder="Fee name (e.g., Lab Fee)"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                        <div className="relative w-24">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                          <input
                            type="number"
                            value={fee.amount}
                            onChange={(e) => handleFeeChange(fee.id, 'amount', parseFloat(e.target.value) || 0)}
                            className="w-full pl-6 pr-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFee(fee.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {formData.additionalFees.length === 0 && (
                      <p className="text-sm text-gray-400 italic">No additional fees</p>
                    )}
                  </div>
                </div>

                {/* Total Preview */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Total Price:</span>
                    <span className="font-semibold text-gray-900">${totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Trial & Cancellation Section */}
              <div className="pb-4 border-b border-gray-100">
                <h3 className="text-sm font-medium text-gray-700 mb-4">Trial & Cancellation</h3>

                {/* Free Trial */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Free Trial Period
                  </label>
                  <select
                    value={formData.trialDays}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      trialDays: parseInt(e.target.value),
                      freeTrial: parseInt(e.target.value) > 0,
                    }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {trialPeriods.map(period => (
                      <option key={period.value} value={period.value}>{period.label}</option>
                    ))}
                  </select>
                </div>

                {/* Auto Cancellation */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="text-sm font-medium text-gray-700">
                      Auto Cancellation
                    </label>
                    <button type="button" className="text-gray-400 hover:text-gray-600">
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </div>
                  <select
                    value={formData.autoCancellation}
                    onChange={(e) => setFormData(prev => ({ ...prev, autoCancellation: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {autoCancellationOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  Activate Plan
                </label>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, active: !prev.active }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    formData.active ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      formData.active ? 'right-1' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {isEditMode ? 'Save Changes' : 'Create Plan'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
